import {
  AttendanceEmployeesSettingExtraHoursBaselineEnum,
  AttendanceEmployeesSettingExtraHoursTransferFrequencyEnum,
  AttendanceEnumsBalanceCarryoverResetFrequencyEnum,
  AttendanceEnumsOvertimePoolUpdateFrequencyEnum,
  AttendanceOvertimePool,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

const getTransferFrequencyLabel = (
  value: AttendanceEmployeesSettingExtraHoursTransferFrequencyEnum
): string => {
  return getTransferFrequencyOptions().find((opt) => opt.value === value)?.label ?? value
}

const getResetFrequencyLabel = (
  value: AttendanceEnumsBalanceCarryoverResetFrequencyEnum
): string => {
  return getResetFrequencyOptions().find((opt) => opt.value === value)?.label ?? value
}

export const validateTransferAndResetCompatibility = (
  transferFrequency: AttendanceEmployeesSettingExtraHoursTransferFrequencyEnum,
  resetFrequency: AttendanceEnumsBalanceCarryoverResetFrequencyEnum
): { isValid: boolean; errorMessage?: string } => {
  const isValidCombination = (validOptions: string[]) => validOptions.includes(resetFrequency)

  const errorMessage = i18n.t(
    'time_tracking.settings.work_time_categories.extra_hours_calculation.transfer_frequency.validation',
    {
      transferFrequency: getTransferFrequencyLabel(transferFrequency).toLowerCase(),
      resetFrequency: getResetFrequencyLabel(resetFrequency).toLowerCase(),
    }
  )

  switch (transferFrequency) {
    case 'daily':
      if (!isValidCombination(['weekly', 'monthly', 'yearly', 'never'])) {
        return { isValid: false, errorMessage }
      }
      break
    case 'weekly':
      if (!isValidCombination(['weekly', 'never'])) {
        return { isValid: false, errorMessage }
      }
      break
    case 'monthly':
      if (!isValidCombination(['monthly', 'yearly', 'never'])) {
        return { isValid: false, errorMessage }
      }
      break
  }

  return { isValid: true }
}

export const getBaselineOptions = (): Array<{
  label: string
  value: AttendanceEmployeesSettingExtraHoursBaselineEnum
  description: string
}> => [
  {
    label: i18n.t(
      'time_tracking.settings.work_time_categories.extra_hours_calculation.baseline.planned_hours'
    ),
    value: 'planned_hours',
    description: i18n.t(
      'time_tracking.settings.work_time_categories.extra_hours_calculation.baseline.planned_hours_description'
    ),
  },
  {
    label: i18n.t(
      'time_tracking.settings.work_time_categories.extra_hours_calculation.baseline.agreement_hours'
    ),
    value: 'contract_hours',
    description: i18n.t(
      'time_tracking.settings.work_time_categories.extra_hours_calculation.baseline.agreement_hours_description'
    ),
  },
]

export const getTransferFrequencyOptions = (): Array<{
  label: string
  value: AttendanceEmployeesSettingExtraHoursTransferFrequencyEnum
}> => [
  {
    label: i18n.t(
      'time_tracking.settings.work_time_categories.extra_hours_calculation.transfer_frequency.options.daily'
    ),
    value: 'daily',
  },
  {
    label: i18n.t(
      'time_tracking.settings.work_time_categories.extra_hours_calculation.transfer_frequency.options.weekly'
    ),
    value: 'weekly',
  },
  {
    label: i18n.t(
      'time_tracking.settings.work_time_categories.extra_hours_calculation.transfer_frequency.options.monthly'
    ),
    value: 'monthly',
  },
]

export const getResetFrequencyOptions = (): Array<{
  label: string
  value: AttendanceEnumsBalanceCarryoverResetFrequencyEnum
}> => [
  {
    label: i18n.t(
      'time_tracking.settings.work_time_categories.extra_hours_calculation.boh_reset_frequency.options.weekly'
    ),
    value: 'weekly',
  },
  {
    label: i18n.t(
      'time_tracking.settings.work_time_categories.extra_hours_calculation.boh_reset_frequency.options.monthly'
    ),
    value: 'monthly',
  },
  {
    label: i18n.t(
      'time_tracking.settings.work_time_categories.extra_hours_calculation.boh_reset_frequency.options.yearly'
    ),
    value: 'yearly',
  },
  {
    label: i18n.t(
      'time_tracking.settings.work_time_categories.extra_hours_calculation.boh_reset_frequency.options.never'
    ),
    value: 'never',
  },
]

export const getResetFrequencyPeriod = (type: string): string => {
  switch (type) {
    case 'weekly':
      return i18n.t(
        'time_tracking.settings.work_time_categories.extra_hours_calculation.boh_reset_frequency.options.weekly'
      )
    case 'monthly':
      return i18n.t(
        'time_tracking.settings.work_time_categories.extra_hours_calculation.boh_reset_frequency.options.monthly'
      )
    case 'yearly':
      return i18n.t(
        'time_tracking.settings.work_time_categories.extra_hours_calculation.boh_reset_frequency.options.yearly'
      )
    default:
      return i18n.t(
        'time_tracking.settings.work_time_categories.extra_hours_calculation.boh_reset_frequency.options.monthly'
      )
  }
}

const getFrequencyValue = (
  frequency:
    | AttendanceEmployeesSettingExtraHoursTransferFrequencyEnum
    | AttendanceEnumsOvertimePoolUpdateFrequencyEnum
): number => {
  switch (frequency) {
    case 'daily':
      return 1
    case 'weekly':
      return 2
    case 'monthly':
      return 3
    default:
      return 0
  }
}

export const isTransferFrequencyCompatibleWithPools = (
  transferFrequency: AttendanceEmployeesSettingExtraHoursTransferFrequencyEnum,
  overtimePools: AttendanceOvertimePool[]
): boolean => {
  const transferFrequencyValue = getFrequencyValue(transferFrequency)

  for (const pool of overtimePools) {
    const poolFrequencyValue = getFrequencyValue(pool.updateFrequency)
    if (transferFrequencyValue > poolFrequencyValue) {
      return false
    }
  }

  return true
}
